import React from 'react';
import PropTypes from 'prop-types';
import { Avatar } from '@mui/material';
import { getInitials, getAvatarColor } from '../utils/avatarUtils';

/**
 * InitialsAvatar - Display user initials in colored circle
 *
 * A reusable avatar component that displays user initials with a deterministic
 * brand-colored background. The same user always gets the same color.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.user - User object with id, firstName, lastName, userName
 * @param {string} props.user.id - User ID (required for color determination)
 * @param {string} [props.user.firstName] - User's first name
 * @param {string} [props.user.lastName] - User's last name
 * @param {string} [props.user.userName] - User's username
 * @param {string} [props.size='md'] - Size: 'sm', 'md', 'lg', 'xl'
 * @param {Object} [props.sx] - Additional MUI sx styles
 *
 * @example
 * <InitialsAvatar user={user} size="sm" />
 * <InitialsAvatar user={user} size="xl" sx={{ margin: 2 }} />
 */
const InitialsAvatar = ({ user, size = 'md', sx, ...props }) => {
  // Size mapping
  const sizeMap = {
    sm: { width: 32, height: 32, fontSize: '0.875rem' },
    md: { width: 40, height: 40, fontSize: '1rem' },
    lg: { width: 56, height: 56, fontSize: '1.25rem' },
    xl: { width: 72, height: 72, fontSize: '1.5rem' }
  };

  const sizeStyles = sizeMap[size] || sizeMap.md;

  // Extract initials and determine background color
  const initials = getInitials(user);
  const backgroundColor = getAvatarColor(user?.id);

  // Build full name for accessibility
  const fullName = user?.firstName && user?.lastName
    ? `${user.firstName} ${user.lastName}`
    : user?.userName || 'User';

  return (
    <Avatar
      sx={{
        ...sizeStyles,
        backgroundColor,
        color: '#FFFFFF',
        fontWeight: 'bold',
        ...(sx || {})
      }}
      aria-label={`Profile picture for ${fullName}`}
      {...props}
    >
      {initials}
    </Avatar>
  );
};

InitialsAvatar.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string.isRequired,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    userName: PropTypes.string
  }).isRequired,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  sx: PropTypes.object
};

export default InitialsAvatar;
